import React, { useEffect, useState, useRef, useCallback } from 'react';

import { Controller, useFormContext } from 'react-hook-form';
import * as S from './styles';
import KogCheckbox from '../../checkbox/kogCheckbox';
import BoxGrid from '../../boxGrid/boxGrid';
import { IGetByInsurer } from '@/main/models/response';
import { SaveInsurerCompanyByValuator } from '@/main/models/request/valuatorRequest';
import SecondaryButton from '../../button/secondaryButton';
import { getTranslateByKey } from '@/main/services/utils';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';

interface IKOGInsurerCompanyBoxGridMultiCheckboxProps {
  rowHeaders: string[] | React.ReactNode[];
  dataInsurersCompany: IGetByInsurer[];
  isStickHeader?: boolean;
  onSubmitSave: (request: SaveInsurerCompanyByValuator) => void
  isLoading: boolean
  onLoadMore?: (pageIndex: number) => Promise<IGetByInsurer[]>
}

interface IGridData {
  data: IGetByInsurer[];
  totalItems: number;
  isLoading: boolean;
}

const INITIAL_GRID_STATE: IGridData = {
  totalItems: 0,
  isLoading: false,
  data: []
};

function KOGInsurerCompanyBoxGridMultiCheckbox({
  rowHeaders,
  dataInsurersCompany,
  isStickHeader = true,
  onSubmitSave,
  isLoading = false,
  onLoadMore
}: IKOGInsurerCompanyBoxGridMultiCheckboxProps) {
  const { control, getValues, setValue } = useFormContext<SaveInsurerCompanyByValuator>();
  const [gridData, setGridData] = useState<IGridData>({
    ...INITIAL_GRID_STATE,
    data: dataInsurersCompany,
    totalItems: dataInsurersCompany.length
  });
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [pageIndex, setPageIndex] = useState<number>(PaginationEnum.PageIndexIntial);
  const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState<boolean>(false);
  const [hasShowMore, setHasShowMore] = useState<boolean>(false);
  const observer = useRef<IntersectionObserver>();
  const loadingRef = useCallback((node: HTMLDivElement) => {
    if (isLoadingInfiniteScroll) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasShowMore && onLoadMore) {
        loadMoreData();
      }
    });

    if (node) observer.current.observe(node);
  }, [isLoadingInfiniteScroll, hasShowMore, onLoadMore]);

  const loadMoreData = async () => {
    if (!onLoadMore || isLoadingInfiniteScroll) return;

    setIsLoadingInfiniteScroll(true);
    try {
      const nextPage = pageIndex + 1;
      const newData = await onLoadMore(nextPage);

      if (newData && newData.length > 0) {
        setGridData(prev => ({
          ...prev,
          data: [...prev.data, ...newData],
          totalItems: prev.totalItems + newData.length
        }));
        setPageIndex(nextPage);
        setHasShowMore(newData.length === PaginationEnum.PageSize15);
      } else {
        setHasShowMore(false);
      }
    } catch (error) {

    } finally {
      setIsLoadingInfiniteScroll(false);
    }
  };

  const handleToggleSelect = (index: number, checked: boolean) => {
    const insurer = gridData.data[index];
    const newSelectedItems = checked
      ? [...selectedItems, insurer.guid]
      : selectedItems.filter(guid => guid !== insurer.guid);
    setSelectedItems(newSelectedItems);
  };

  const handleSelectAll = (checked: boolean) => {
    const newSelectedItems = checked ? gridData.data.map(insurer => insurer.guid) : [];
    setSelectedItems(newSelectedItems);
  };

  function onSave() {
    const request: SaveInsurerCompanyByValuator = {
      guidValuator: '',
      guidInsurer: selectedItems
    }

    onSubmitSave(request)
    setSelectedItems([])
  }

  useEffect(() => {
    setGridData({
      ...INITIAL_GRID_STATE,
      data: dataInsurersCompany,
      totalItems: dataInsurersCompany.length
    });
    setSelectedItems([]);
    setPageIndex(PaginationEnum.PageIndexIntial);
    setHasShowMore(dataInsurersCompany.length === PaginationEnum.PageSize15);
  }, [dataInsurersCompany]);

  const rowHeadersInsurerCompany = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <S.TitleType style={{ fontWeight: 'bold', color: 'gray' }}>
        {getTranslateByKey('information.insurer')}
      </S.TitleType>
      <KogCheckbox
        checked={selectedItems.length === gridData.data.length && gridData.data.length > 0}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          handleSelectAll(event.target.checked);
        }}
        id="id-registerInsurer-checkbox-selectAll"
      />
    </div>
  );

  const rowContentInsurerCompany = gridData.data.map((insurer, index) => (
    <S.ContainerHeaderGrid key={insurer.guid} style={{ backgroundColor: 'white' }}>
      <S.TitleType>
        {insurer.companyName}
      </S.TitleType>
      <Controller
        control={control}
        name={`guidInsurer.${index}`}
        render={({ field: fieldController }) => (
          <KogCheckbox
            {...fieldController}
            checked={selectedItems.includes(insurer.guid)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const checked = event.target.checked;
              handleToggleSelect(index, checked);
              fieldController.onChange(checked ? insurer.guid : '');
            }}
            id={`id-registerInsurer-checkbox-${insurer.guid}`}
          />
        )}
      />
    </S.ContainerHeaderGrid>
  ));

  const propsTable = isStickHeader === true ? {
    stickyHeader: true
  } : {}

  const ID_SCROLLABLE_TARGET = 'id-scrollable-target-insurerCompanySidebar';

  return (
    <>
      <S.ContainerScroll id={ID_SCROLLABLE_TARGET}>
        <BoxGrid
          rowHeaders={[rowHeadersInsurerCompany]}
          propsTable={propsTable}
          typeGrid="default"
          sizeCell="medium"
          marginCustom='0.3rem 0 0 0'
        >
          {rowContentInsurerCompany}
          {hasShowMore && (
            <div ref={loadingRef} style={{ padding: '16px', textAlign: 'center' }}>
              {isLoadingInfiniteScroll && <S.Spinner />}
            </div>
          )}
        </BoxGrid>
      </S.ContainerScroll>

      <S.AreaButtons>
        <SecondaryButton
          id="id-InsurerCompanyRegister-button-save"
          onClick={onSave}
          disabled={isLoading || selectedItems.length === 0}
          isLoading={isLoading}
        >
          {getTranslateByKey('information.save')}
        </SecondaryButton>
      </S.AreaButtons>
    </>
  );
}

export default KOGInsurerCompanyBoxGridMultiCheckbox;
